import React, { useEffect, useMemo, useState } from 'react';
import {
   Box,
   Button,
   Checkbox,
   Flex,
   FormControl,
   FormLabel,
   Modal,
   ModalBody,
   ModalContent,
   ModalFooter,
   ModalHeader,
   ModalOverlay,
   IconButton,
   HStack,
   VStack,
   useColorModeValue,
   Divider,
   Text,
   Stack,
   SimpleGrid,
   GridItem,
   Alert,
   AlertIcon,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { renderDynamicDropdown } from '../../../../components/UIcomponents/DynamicDropdown';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from 'contexts';
import { getAccount } from '../../../../redux/actions/account';
import { getItem } from '../../../../redux/actions/item';
import { getCategory } from '../../../../redux/actions/category';
import { getBrand } from '../../../../redux/actions/brand';
import { getSeries } from '../../../../redux/actions/series';
import { getDriver } from '../../../../redux/actions/driver';
import { getVehicle } from '../../../../redux/actions/vehicle';
import { getSize } from '../../../../redux/actions/size';
import { getItemGroup } from '../../../../redux/actions/item';
import { getColor } from '../../../../redux/actions/color';
import { getTransport } from '../../../../redux/actions/transport';
import { postMergeCode } from '../../../../redux/actions/utility';
import { getWarehouse } from '../../../../redux/actions/warehouse';

export default function MergeMastersDialog({ isMergeCode, onClose, onMergeComplete }) {
   const [masterEntry, setMasterEntry] = useState(null);
   const [deleteFromCode, setDeleteFromCode] = useState(false);
   const [mergeOpeningBalance, setMergeOpeningBalance] = useState(false);
   const [fromCode, setFromCode] = useState('');
   const [toCode, setToCode] = useState('');

   // Get all master data from Redux
   const accounts = useSelector((state) => state.account?.list || []);
   const items = useSelector((state) => state.item?.list || []);
   const colors = useSelector((state) => state.color?.list || []);
   const transporters = useSelector((state) => state.transport?.list || []);
   const categories = useSelector((state) => state.category?.list || []);
   const brands = useSelector((state) => state.brand?.list || []);
   const series = useSelector((state) => state.series?.list || []);
   const drivers = useSelector((state) => state.driver?.list || []);
   const vehicles = useSelector((state) => state.vehicle?.list || []);
   const sizes = useSelector((state) => state.size?.list || []);
   const itemGroups = useSelector((state) => state.itemGroup?.list || []);
   const screens = useSelector((state) => state.screen?.list || []);
   const warehouses = useSelector((state) => state.warehouse?.list || []);
   const dispatch = useDispatch();
   const { user } = useUser();

   // Enhanced color mode values
   const bgColor = useColorModeValue('gray.50', 'gray.800');
   const borderColor = useColorModeValue('gray.200', 'gray.600');
   const textColor = useColorModeValue('gray.700', 'gray.200');
   const modalContentBg = useColorModeValue('white', 'gray.800');
   const sectionBg = useColorModeValue('gray.25', 'gray.750');
   const labelColor = useColorModeValue('gray.600', 'gray.300');

   const masterOptions = [
      { id: 1, name: 'Account' },
      { id: 2, name: 'Items' },
      { id: 3, name: 'Color' },
      { id: 4, name: 'Transporter' },
      { id: 5, name: 'Category' },
      { id: 6, name: 'Brand' },
      { id: 7, name: 'Series' },
      { id: 8, name: 'Driver' },
      { id: 9, name: 'Vehicle' },
      { id: 10, name: 'Size' },
      { id: 11, name: 'Item Group' },
      { id: 12, name: 'Screen' },
      { id: 13, name: 'Warehouse' },
   ];

   // Validation: Check if from code and to code are the same
   const isSameCodeSelected = fromCode && toCode && fromCode === toCode;

   // Form validation
   const isFormValid = masterEntry && fromCode && toCode && !isSameCodeSelected;

   // Reset form function
   const resetForm = () => {
      setMasterEntry(null);
      setFromCode('');
      setToCode('');
      setDeleteFromCode(false);
      setMergeOpeningBalance(false);
   };

   // Reset form when modal closes
   useEffect(() => {
      if (!isMergeCode) {
         resetForm();
      }
   }, [isMergeCode]);

   // Fetch data based on master type
   useEffect(() => {
      // Reset codes when master changes
      setFromCode('');
      setToCode('');

      const companyId = user?.loginhistory?.companyId || localStorage.getItem('companyId');

      switch (masterEntry) {
         case 1: // Account
            dispatch(getAccount({ compId: companyId, companyIds: companyId, status: true }));
            break;
         case 2: // Items
            dispatch(getItem({ companyId: companyId, companyIds: companyId, status: true }));
            break;
         case 3: // Color
            dispatch(getColor());
            break;
         case 4: // Transporter
            dispatch(getTransport());
            break;
         case 5: // Category
            dispatch(getCategory());
            break;
         case 6: // Brand
            dispatch(getBrand());
            break;
         case 7: // Series
            dispatch(getSeries());
            break;
         case 8: // Driver
            dispatch(getDriver());
            break;
         case 9: // Vehicle
            dispatch(getVehicle());
            break;
         case 10: // Size
            dispatch(getSize());
            break;
         case 11: // Item Group
            dispatch(getItemGroup());
            break;
         case 12: // Screen
            // dispatch(getScreen());
            break;
         case 13: // Warehouse
            dispatch(getWarehouse());
            break;
         default:
            break;
      }
   }, [masterEntry, dispatch, user]);

   // Get options for current master type
   const getCurrentMasterOptions = useMemo(() => {
      const mapOptions = (items, id = 'id', name = 'name') => {
         if (!items || !Array.isArray(items)) return [];
         return items.map((item) => ({
            id: item[id]?.toString() || '',
            name: item[name] || `Unnamed (${item[id]})`,
         }));
      };

      switch (masterEntry) {
         case 1: // Account
            return mapOptions(accounts, 'id', 'name');
         case 2: // Items
            return mapOptions(items, 'id', 'name');
         case 3: // Color
            return mapOptions(colors, 'id', 'name');
         case 4: // Transporter
            return mapOptions(transporters, 'id', 'name');
         case 5: // Category
            return mapOptions(categories, 'id', 'name');
         case 6: // Brand
            return mapOptions(brands, 'id', 'name');
         case 7: // Series
            return mapOptions(series, 'id', 'name');
         case 8: // Driver
            return mapOptions(drivers, 'id', 'name');
         case 9: // Vehicle
            return mapOptions(vehicles, 'id', 'name');
         case 10: // Size
            return mapOptions(sizes, 'id', 'name');
         case 11: // Item Group
            return mapOptions(itemGroups, 'id', 'name');
         case 12: // Screen
            return mapOptions(screens, 'id', 'name');
         case 13: // Warehouse
            return mapOptions(warehouses, 'id', 'name');
         default:
            return [];
      }
   }, [
      masterEntry,
      accounts,
      items,
      colors,
      transporters,
      categories,
      brands,
      series,
      drivers,
      vehicles,
      sizes,
      itemGroups,
      screens,
      warehouses,
   ]);

   const handleMerge = () => {
      if (!isFormValid) return;

      const companyId = user?.loginhistory?.companyId || parseInt(localStorage.getItem('companyId'));
      const payload = {
         masterEntry,
         fromCode: parseInt(fromCode),
         toCode: parseInt(toCode),
         deleteFromCode,
         mergeOpeningBalance,
         companyId,
      };

      dispatch(postMergeCode(payload))
         .then(() => {
            if (onMergeComplete) {
               onMergeComplete(payload);
            }
            if (masterEntry === 1) {
               console.log("work for account");
            }
            resetForm();
            onClose();
         })
         .catch((error) => {
            console.error('Merge failed:', error);
         });
   };

   const handleClose = () => {
      resetForm();
      onClose();
   };

   const handleSelectMaster = (value) => {
      const numValue = Number(value);
      setMasterEntry(numValue);
   };

   const handleFromCodeChange = (value) => {
      setFromCode(value);
   };

   const handleToCodeChange = (value) => {
      setToCode(value);
   };

   return (
      <>
         <Modal isOpen={isMergeCode} onClose={handleClose} isCentered size="xl">
            <ModalOverlay />
            <ModalContent
               borderWidth="1px"
               borderColor={borderColor}
               boxShadow="2xl"
               borderRadius="xl"
               bg={modalContentBg}
               maxW="600px">
               {/* Enhanced Header */}
               <ModalHeader
                  fontSize="lg"
                  fontWeight="semibold"
                  py="4"
                  px="6"
                  borderTopRadius="xl"
                  borderBottomWidth="1px"
                  borderBottomColor={borderColor}>
                  <Flex justify="space-between" align="center">
                     <HStack spacing="3">
                        <Text>Merge Code</Text>
                     </HStack>
                     <IconButton
                        aria-label="Close"
                        icon={<CloseIcon />}
                        size="sm"
                        variant="ghost"
                        _hover={{ bg: 'whiteAlpha.200' }}
                        onClick={handleClose}
                     />
                  </Flex>
               </ModalHeader>

               <ModalBody p="6" bg={bgColor}>
                  <VStack spacing="6" align="stretch">
                     {/* Master Entry Selection */}
                     <Box p="4" bg={sectionBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <VStack spacing="4" align="stretch">
                           <FormControl>
                              <FormLabel fontSize="sm" fontWeight="medium" color={labelColor} mb="2">
                                 Master Entry:
                              </FormLabel>
                              <GridItem className="seperator-width" mt={5}>
                                 {renderDynamicDropdown(
                                    'master',
                                    'Master',
                                    masterOptions,
                                    {
                                       value: masterEntry,
                                       onChange: handleSelectMaster,
                                    },
                                    {},
                                    textColor,
                                    false,
                                    'Select master',
                                 )}
                              </GridItem>
                           </FormControl>
                        </VStack>
                     </Box>

                     <Divider />

                     {/* Code Input Section */}
                     <Box p="4" bg={sectionBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <VStack spacing="4" align="stretch">
                           <SimpleGrid columns={1} spacing="4">
                              {/* From Code */}
                              <FormControl>
                                 <GridItem className="seperator-width">
                                    {renderDynamicDropdown(
                                       'fromCode',
                                       'From Code',
                                       getCurrentMasterOptions,
                                       {
                                          value: fromCode,
                                          onChange: handleFromCodeChange,
                                       },
                                       {},
                                       textColor,
                                       false,
                                       'Select from code',
                                    )}
                                 </GridItem>
                              </FormControl>

                              {/* To Code */}
                              <FormControl>
                                 <GridItem className="seperator-width">
                                    {renderDynamicDropdown(
                                       'toCode',
                                       'To Code',
                                       getCurrentMasterOptions,
                                       {
                                          value: toCode,
                                          onChange: handleToCodeChange,
                                       },
                                       {},
                                       textColor,
                                       false,
                                       'Select to code',
                                    )}
                                 </GridItem>
                              </FormControl>
                           </SimpleGrid>

                           {/* Validation Error Message */}
                           {isSameCodeSelected && (
                              <Alert status="error" borderRadius="md" fontSize="sm">
                                 <AlertIcon />
                                 From Code and To Code cannot be the same. Please select different codes.
                              </Alert>
                           )}
                        </VStack>
                     </Box>

                     <Divider />

                     {/* Options Section */}
                     <Box p="4" bg={sectionBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <VStack spacing="4" align="stretch">
                           <Stack spacing="3">
                              <Checkbox
                                 isChecked={deleteFromCode}
                                 onChange={(e) => setDeleteFromCode(e.target.checked)}
                                 size="md"
                                 colorScheme="red"
                                 color={textColor}
                                 fontWeight="medium">
                                 <VStack align="start" spacing="1">
                                    <Text fontSize="sm">Delete From-Code After Merging</Text>
                                    <Text fontSize="xs" color={labelColor}>
                                       This will permanently remove the source code after merge completion
                                    </Text>
                                 </VStack>
                              </Checkbox>

                              <Checkbox
                                 isChecked={mergeOpeningBalance}
                                 onChange={(e) => setMergeOpeningBalance(e.target.checked)}
                                 size="md"
                                 colorScheme="blue"
                                 color={textColor}
                                 fontWeight="medium">
                                 <VStack align="start" spacing="1">
                                    <Text fontSize="sm">Merge Opening Balance</Text>
                                    <Text fontSize="xs" color={labelColor}>
                                       Include opening balance in the merge operation
                                    </Text>
                                 </VStack>
                              </Checkbox>
                           </Stack>
                        </VStack>
                     </Box>
                  </VStack>
               </ModalBody>

               {/* Enhanced Footer */}
               <ModalFooter
                  justifyContent="space-between"
                  py="4"
                  px="6"
                  bg={bgColor}
                  borderTopWidth="1px"
                  borderTopColor={borderColor}
                  borderBottomRadius="xl">
                  <HStack spacing="3">
                     <Button
                        onClick={handleMerge}
                        colorScheme="green"
                        size="md"
                        minW="32"
                        fontWeight="semibold"
                        isDisabled={!isFormValid}
                        _focus={{ outlineColor: 'blue.500 ' }}
                        _hover={{
                           transform: isFormValid ? 'translateY(-1px)' : 'none',
                           boxShadow: isFormValid ? 'md' : 'none',
                        }}>
                        Merge
                     </Button>
                     <Button
                        onClick={handleClose}
                        variant="outline"
                        size="md"
                        minW="32"
                        borderColor={borderColor}
                        color={textColor}
                        fontWeight="semibold"
                        _focus={{ outlineColor: 'blue.500 ' }}
                        _hover={{
                           bg: useColorModeValue('gray.50', 'gray.700'),
                           borderColor: useColorModeValue('gray.300', 'gray.500'),
                           transform: 'translateY(-1px)',
                        }}>
                        Cancel
                     </Button>
                  </HStack>
               </ModalFooter>
            </ModalContent>
         </Modal>
      </>
   );
}
